import { gql } from "@apollo/client";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { Loader } from "@mantine/core";
import { modals } from "@mantine/modals";
import { graphql } from "gql.tada";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import {
  EventTicketConfiguration,
  Property,
  ShopCurrencies,
} from "../../../../gql/graphql";
import { AutoDataTable } from "../../components/AutoDataTable";
import { CreationForm } from "../../components/form/CreationForm";
import { constructCustomFieldsForm } from "../../components/form/CustomFieldsForm";
import { ListPageHeader } from "../../components/PageHeader";
import { useConfirmModal } from "../../components/ConfirmModal";

const EVENT_TICKETS_QUERY = graphql(`
  query eventTicketConfigurations($id: ID!) {
    eventTicketConfigurations(eventId: $id) {
      id
      price
      currentCount
      maxCount
      currency
      name
      description
      seatTypeId
      buyable
    }
  }
`);

const EVENT_TICKET_QUERY = graphql(`
  query eventTicketConfiguration($id: ID!) {
    eventTicketConfiguration(id: $id) {
      id
      price
      currency
      name
      description
      maxCount
      seatTypeId
      buyable
      configuration {
        customFields {
          name
          order
          property
          public
          required
          type
          unique
          visibility
          editability
        }
      }
    }
  }
`);

const EVENT_VENUE_SEAT_TYPES_QUERY = graphql(`
  query eventVenueSeatTypes($id: ID!) {
    eventVenueSeatTypes(venueId: $id, page: { first: 100 }) {
      nodes {
        id
        name
      }
    }
  }
`);

const EVENT_VENUE_SEATS_COUNT_QUERY = graphql(`
  query eventVenueSeatsCount($id: ID!, $seatTypeId: ID) {
    eventVenueSeats(venueId: $id, page: { first: 1 }, seatTypeId: $seatTypeId) {
      totalCount
    }
  }
`);

const EVENT_QUERY = graphql(`
  query event($id: ID!) {
    event(id: $id) {
      eventVenueId
    }
  }
`);

const EventTicketUpdateModal = ({
  ticketConfigurationId,
  eventId,
}: {
  ticketConfigurationId?: string;
  eventId: string;
}) => {
  const [updatedMaxCount, setUpdatedMaxCount] = useState<number | null>(null);
  const { confirm, modal } = useConfirmModal();
  const { loading, data } = useQuery<any>(EVENT_TICKET_QUERY, {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    variables: { id: ticketConfigurationId! },
    skip: !ticketConfigurationId,
  });
  const { loading: eventLoading, data: eventData } = useQuery<any>(
    EVENT_QUERY,
    {
      variables: { id: eventId },
    },
  );
  const { loading: seatTypesLoading, data: seatTypesData } = useQuery<any>(
    EVENT_VENUE_SEAT_TYPES_QUERY,
    {
      variables: { id: eventData?.event?.eventVenueId ?? "" },
      skip: !eventData?.event?.eventVenueId,
    },
  );
  const [getSeatsCount] = useLazyQuery<any>(EVENT_VENUE_SEATS_COUNT_QUERY);
  const [update] = useMutation<any>(gql`
    mutation ($id: ID!, $input: EventTicketConfigurationUpdateInput!) {
      eventTicketConfigurationUpdate(
        ticketConfigurationId: $id
        input: $input
      ) {
        id
      }
    }
  `);
  const [create] = useMutation<any>(gql`
    mutation ($input: EventTicketConfigurationCreateInput!) {
      eventTicketConfigurationCreate(input: $input) {
        id
      }
    }
  `);

  if (loading || seatTypesLoading || eventLoading) {
    return <Loader />;
  }

  return (
    <>
      {modal}
      <CreationForm
        schemaDefinition={{
          name: {
            label: "Name",
            type: "INPUT",
            inputType: "text",
            yupConfig: yup.string().required(),
            defaultValue: data?.eventTicketConfiguration?.name,
          },
          description: {
            label: "Description",
            type: "TEXTAREA",
            yupConfig: yup.string().required(),
            defaultValue: data?.eventTicketConfiguration?.description,
          },
          price: {
            label: "Price",
            type: "INPUT",
            inputType: "number",
            yupConfig: yup.number().required(),
            defaultValue: (data?.eventTicketConfiguration?.price ?? 0) / 100,
          },
          currency: {
            label: "Currency",
            type: "SELECT",
            options: Object.values(ShopCurrencies).map((v) => ({
              label: v,
              value: v,
            })),
            yupConfig: yup.string().required(),
            defaultValue: data?.eventTicketConfiguration?.currency,
          },
          maxCount: {
            label: "Max count",
            type: "INPUT",
            inputType: "number",
            yupConfig: yup.number().required(),
            defaultValue: data?.eventTicketConfiguration?.maxCount,
            onChange: (value) => {
              setUpdatedMaxCount(Number(value));
            },
            value: updatedMaxCount,
          },
          seatTypeId: {
            label: "Seat type",
            type: "SELECT",
            options:
              seatTypesData?.eventVenueSeatTypes?.nodes.map((v: any) => ({
                label: v.name,
                value: v.id,
              })) ?? [],
            yupConfig: yup.string().nullable(),
            defaultValue: data?.eventTicketConfiguration?.seatTypeId ?? null,
            onChange: async (value) => {
              const response = await getSeatsCount({
                variables: {
                  id: eventData?.event?.eventVenueId ?? "",
                  seatTypeId: value,
                },
              });
              setUpdatedMaxCount(
                response.data?.eventVenueSeats.totalCount ?? 0,
              );
            },
          },
          buyable: {
            label: "Buyable",
            type: "CHECKBOX",
            defaultValue: data?.eventTicketConfiguration?.buyable === true,
            yupConfig: yup.boolean().required(),
          },
          customFields: constructCustomFieldsForm({
            defaultValue: data?.eventTicketConfiguration?.configuration
              ?.customFields as Property[],
            label: "Custom fields",
          }),
        }}
        onSubmit={async (datas) => {
          if (ticketConfigurationId) {
            if (
              datas.seatTypeId !== data?.eventTicketConfiguration?.seatTypeId
            ) {
              const confirmed = await confirm({
                title: "Change Seat Type",
                message:
                  "Are you sure you want to change the seat type? This will affect all reservations for this ticket configuration. Reservation tickets will still be accessible, but they will not be assigned to a seat anymore.",
                confirmLabel: "Change Seat Type",
                type: "warning",
              });

              if (!confirmed) {
                return;
              }
            }

            await update({
              variables: {
                id: ticketConfigurationId,
                input: {
                  name: datas.name,
                  description: datas.description,
                  price: datas.price * 100,
                  currency: datas.currency,
                  maxCount: datas.maxCount,
                  seatTypeId:
                    !datas.seatTypeId || datas.seatTypeId === ""
                      ? null
                      : datas.seatTypeId,
                  buyable: datas.buyable,
                  configuration: {
                    customFields: datas.customFields,
                  },
                },
              },
            }).then((data) => {
              if (data?.error) {
                throw new Error(data.error.message);
              }
            });
          } else {
            await create({
              variables: {
                input: {
                  eventId,
                  name: datas.name,
                  description: datas.description,
                  price: datas.price * 100,
                  currency: datas.currency,
                  maxCount: datas.maxCount,
                  seatTypeId:
                    !datas.seatTypeId || datas.seatTypeId === ""
                      ? null
                      : datas.seatTypeId,
                  buyable: datas.buyable,
                  configuration: {
                    customFields: datas.customFields,
                  },
                },
              },
            }).then((data) => {
              if (data?.error) {
                throw new Error(data.error.message);
              }
            });
          }
          modals.closeAll();
        }}
      />
    </>
  );
};

export const EventTickets = () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const eventId = useParams().eventId!;
  const navigate = useNavigate();

  return (
    <div className="wp-fade-in">
      <ListPageHeader
        title="Ticket Configurations"
        createLabel="Create Ticket"
        onCreate={() => {
          modals.open({
            children: <EventTicketUpdateModal eventId={eventId} />,
            onClose() {
              navigate(0);
            },
          });
        }}
        showBreadcrumbs={false}
      />
      <AutoDataTable
        createButtonText={false}
        deleteMutation={gql`
          mutation ($id: ID!) {
            eventTicketConfigurationDelete(ticketConfigurationId: $id) {
              id
            }
          }
        `}
        linkClick={(item) => {
          modals.open({
            children: (
              <EventTicketUpdateModal
                eventId={eventId}
                ticketConfigurationId={item.id}
              />
            ),
            onClose() {
              navigate(0);
            },
          });
        }}
        query={EVENT_TICKETS_QUERY}
        queryVariables={{
          id: eventId,
        }}
        columns={[
          {
            accessor: "name",
            title: "Name",
          },
          {
            accessor: "description",
            title: "Description",
          },
          {
            accessor: "price",
            title: "Price",
            rawElement: (item: EventTicketConfiguration) => (
              <>
                {(item.price / 100).toFixed(2)}
                {item.currency}
              </>
            ),
          },
          {
            accessor: "count",
            title: "Available tickets",
            rawElement: (item) => (
              <>
                {item.currentCount}/{item.maxCount}
              </>
            ),
          },
        ]}
      />
    </div>
  );
};
